/*
   Saving an engagement's detector profile.
*/

import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query"

import { toFullProfile, type DetectorProfile } from "@/components/domain/detector-profile"
import { http } from "@/lib/http"
import { qk } from "@/lib/query-keys"

import type { ClientId } from "../clients-data"

import type { ClientOutWire } from "./clients"

export interface EngagementProfileChange {
  clientId: ClientId
  profile: DetectorProfile
}

/**
 * `PATCH /clients/{id}` with the engagement's whole profile.
 *
 * Every key is sent, Automatic included, so a save replaces what is stored
 * rather than merging into it: a setting put back to Automatic has to reach
 * the server as Automatic, not as an absence it could read as "unchanged".
 */
export async function setEngagementProfile(change: EngagementProfileChange): Promise<void> {
  await http.patch<ClientOutWire>(`/api/v1/clients/${change.clientId}`, {
    detector_profile: toFullProfile(change.profile),
  })
}

export function useSetEngagementProfileMutation(): UseMutationResult<
  void,
  Error,
  EngagementProfileChange
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: setEngagementProfile,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.clients.all })
    },
  })
}
